import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { AnalysisResult } from '../../../Clients/ImportClients/useAnalyseCSV';
import { ColumnMapping, normalizeHeader } from '../../../Clients/ImportClients/autoMap';
import { getGeneralSettings } from '../../../../store/settings/selectors';
import { ImportAppointmentOptions } from './ImportAppointmentOptions';

const RAW = 'raw' as const;
const SKIP = 'skip' as const;

const normalizeLookup = normalizeHeader;

interface Props {
    result: AnalysisResult;
    mapping: ColumnMapping;
    options: ImportAppointmentOptions;
    onOptionsChange: (o: ImportAppointmentOptions) => void;
}

const ServiceMappingStep: React.FC<Props> = ({ result, mapping, options, onOptionsChange }) => {
    const t = useTranslations();
    const generalSettings = useSelector(getGeneralSettings);

    const typeColIndex = mapping.fieldMap['type']?.[0] ?? -1;

    const uniqueValues = React.useMemo(() => {
        if (typeColIndex === -1) return [];
        const seen = new Set<string>();
        const values: string[] = [];
        for (const row of result.allRows) {
            const v = row[typeColIndex]?.trim();
            if (v && !seen.has(v)) { seen.add(v); values.push(v); }
        }
        return values;
    }, [result.allRows, typeColIndex]);

    const serviceEntries = React.useMemo(
        () => Object.entries(generalSettings?.services ?? {}).map(([key, s]) => ({ key, name: s.name || key })),
        [generalSettings],
    );

    // On first render, auto-suggest matches; unmatched default to 'raw'
    const initializedRef = React.useRef(false);
    React.useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;

        const updated: Record<string, string | 'raw' | 'skip'> = { ...options.serviceMappings };
        let changed = false;
        for (const value of uniqueValues) {
            const norm = normalizeLookup(value);
            if (norm in updated) continue;
            const match = serviceEntries.find(e =>
                normalizeLookup(e.key) === norm || normalizeLookup(e.name) === norm,
            );
            updated[norm] = match ? match.key : RAW;
            changed = true;
        }
        if (changed) onOptionsChange({ ...options, serviceMappings: updated });
    }, [uniqueValues, serviceEntries]);

    const handleSelect = (value: string, selected: string) => {
        const norm = normalizeLookup(value);
        onOptionsChange({
            ...options,
            serviceMappings: { ...options.serviceMappings, [norm]: selected },
        });
    };

    if (uniqueValues.length === 0) {
        return (
            <div style={{ color: 'var(--text-muted)', padding: 'var(--spacer-M)' }}>—</div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', margin: 0 }}>
                {t(TK.importAppointmentsMapServices)}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflowY: 'auto', maxHeight: '60vh' }}>
                {uniqueValues.map(value => {
                    const norm = normalizeLookup(value);
                    const currentValue = options.serviceMappings[norm] ?? RAW;
                    const isSkipped = currentValue === SKIP;
                    return (
                        <div
                            key={value}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacer-S)',
                                padding: 'var(--spacer-XS) var(--spacer-S)',
                                borderRadius: '6px',
                                border: 'var(--border-thin) solid var(--appLigthGrey)',
                                background: isSkipped ? 'var(--bg-light)' : 'white',
                                opacity: isSkipped ? 0.6 : 1,
                            }}
                        >
                            <span style={{
                                flex: 1,
                                fontSize: '0.875rem',
                                color: 'var(--text-body)',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textDecoration: isSkipped ? 'line-through' : 'none',
                            }}>
                                {value}
                            </span>
                            <select
                                value={currentValue}
                                onChange={e => handleSelect(value, e.target.value)}
                                style={{
                                    fontSize: '0.85rem',
                                    padding: '4px var(--spacer-XS)',
                                    border: 'var(--border-thin) solid var(--appLigthGrey)',
                                    borderRadius: '4px',
                                    color: 'var(--text-body)',
                                    background: 'white',
                                    minWidth: '220px',
                                    maxWidth: '320px',
                                }}
                            >
                                <option value={RAW}>{t(TK.importAppointmentsUseAsIs)}</option>
                                <option value={SKIP}>{t(TK.importAppointmentsSkipAppointments)}</option>
                                {serviceEntries.length > 0 && <option disabled>──────────</option>}
                                {serviceEntries.map(e => (
                                    <option key={e.key} value={e.key}>{e.name}</option>
                                ))}
                            </select>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ServiceMappingStep;

// Pure helpers exported for the modal's skip-step and navigation logic
export function getUniqueServiceValues(allRows: string[][], typeColIndex: number): string[] {
    if (typeColIndex === -1) return [];
    const seen = new Set<string>();
    const values: string[] = [];
    for (const row of allRows) {
        const v = row[typeColIndex]?.trim();
        if (v && !seen.has(v)) { seen.add(v); values.push(v); }
    }
    return values;
}
